import { api } from './client';
// The report's own time column is the same IST clock the bill lists show —
// format.ts imports nothing, so this stays a leaf dependency.
import { billTime } from '../format';

export type PaymentMethod = 'CASH' | 'CARD' | 'UPI';

export const PAYMENT_METHODS: PaymentMethod[] = ['CASH', 'CARD', 'UPI'];

/** How a tender is written wherever staff read it back. */
export function paymentLabel(method: PaymentMethod): string {
  return method === 'CASH' ? 'Cash' : method === 'CARD' ? 'Card' : 'UPI';
}

export interface PosBook {
  id: string;
  title: string;
  sku: string | null;
  isbn13: string | null;
  /** Decimal(10,2) — arrives as a string like "250". Always coerce before maths. */
  price: number | string;
  /**
   * @deprecated Mirror of the shop's stock figure, under the old name.
   *
   * Read stock through `stockCountOf`, never off this field directly — see
   * the note there for which name to trust and why both still arrive.
   */
  storeStock: number | null;
  /** The shop's stock. One figure, shared with the website. */
  stockQuantity: number | null;
  /** @deprecated No longer sent. There is no warehouse pool to report. */
  warehouseStock?: number | null;
  inStock?: boolean;
  coverImageUrl: string | null;
  isActive: boolean;
  /**
   * A damaged "D-copy", sold cheap over the counter and never online.
   *
   * Shown as a tag, never used to filter: the till is the only place these
   * sell, so hiding them here would leave them unsellable everywhere. It
   * matters at the counter because the clean copy of the same title is a
   * separate row at a higher price, and the two are easy to confuse.
   *
   * Optional — an older server does not send it.
   */
  isDCopy?: boolean;
  /**
   * A second-hand copy. It carries the same ISBN as the new book, so one scan
   * returns both and the cashier picks — the USED tag is how they tell which.
   *
   * Optional — an older server does not send it.
   */
  isUsed?: boolean;
  /**
   * The printed price. A title the publisher re-priced is two books under one
   * ISBN, each at its own MRP, and the MRP on the cover is how the cashier
   * knows which one is in hand. Decimal — may arrive as a string.
   *
   * Optional — an older server does not send it.
   */
  mrp?: number | string;
}

const readCount = (value: number | null | undefined): number | null =>
  typeof value === 'number' ? value : null;

/**
 * What the counter may sell.
 *
 * There is one stock figure now: the website and the shop counter both bill
 * from it, wherever the copies physically stand. A copy sold online is a copy
 * this till can no longer hand over, and the other way round.
 *
 * ⚠️ It reads `storeStock` first on purpose, and that is the one line here
 * worth being careful about. `storeStock` is correct against BOTH servers:
 * the current one mirrors the shared figure out under that name, and an older
 * one still running channel pools puts the counter's own pool there — which is
 * exactly what a till talking to that server is allowed to sell. Preferring
 * `stockQuantity` would, on an older server, show staff the ONLINE pool and
 * every sale would be refused at the till. Prod had not taken the shared-stock
 * change when this was written.
 *
 * Once every environment is on shared stock, read `stockQuantity` alone and
 * drop the mirror from the backend. This is its only reader.
 */
export function stockCountOf(book: PosBook): number | null {
  return readCount(book.storeStock) ?? readCount(book.stockQuantity);
}

export function isSellable(book: PosBook): boolean {
  if (!book.isActive) return false;
  const stock = stockCountOf(book);
  // Unknown rather than zero: if the backend ever stops sending the figure,
  // fall back to letting the server decide instead of making the whole
  // catalogue unsellable at the till.
  return stock === null ? book.inStock !== false : stock > 0;
}

/**
 * The MRP to tag a result with, when the list holds another copy of the same
 * ISBN at a different MRP — the old and the new printing of a re-priced title.
 * Null otherwise, so an ordinary row stays as it was.
 */
export function repricedMrpOf(book: PosBook, list: PosBook[]): number | null {
  if (book.mrp === undefined || !book.isbn13) return null;
  const mrp = Number(book.mrp);
  const other = list.some(
    (b) =>
      b.id !== book.id &&
      b.isbn13 === book.isbn13 &&
      b.mrp !== undefined &&
      Number(b.mrp) !== mrp,
  );
  return other ? mrp : null;
}

export function stockLabelOf(book: PosBook): string {
  if (!book.isActive) return 'Unavailable';
  const stock = stockCountOf(book);
  if (stock === null) {
    return book.inStock === false ? 'Out of stock' : 'In stock';
  }
  // No "but there are twenty in the back" any more: there is no back. This
  // number is everything the shop has, so nought here means nought.
  return stock > 0 ? `${stock} in stock` : 'Out of stock';
}

/**
 * A line on a completed bill. The sale endpoints are owned by the backend team,
 * so the reader below tolerates the handful of field names their order shape
 * might use rather than hard-failing the print view over a rename.
 */
export interface PosSaleItem {
  id?: string;
  bookId?: string;
  quantity: number;
  unitPrice?: number;
  price?: number;
  totalPrice?: number;
  lineTotal?: number;
  title?: string;
  bookTitle?: string;
  sku?: string;
  isbn13?: string;
  book?: { id?: string; title?: string; sku?: string | null; isbn13?: string | null } | null;
}

export interface PosSale {
  id: string;
  billNumber?: string;
  orderNumber?: string;
  createdAt?: string;
  paymentMethod?: PaymentMethod;
  subtotal?: number;
  /**
   * Rupees taken off the whole bill at the counter, as a flat sum.
   *
   * Never spread across the lines: the books were sold at their shelf price
   * and the shop took less for the bill, which is why `subtotal` still adds
   * the lines up and `total` is the money that changed hands. Optional — a
   * server older than this field simply omits it, and a bill without one
   * reads as zero.
   */
  discount?: number | string;
  /**
   * A coupon the customer brought, and how much of `discount` it accounts
   * for; the rest is what the counter gave by hand. Optional — a server older
   * than counter coupons omits both, and a bill without them had no coupon.
   */
  couponCode?: string | null;
  couponDiscount?: number | string;
  /** The coupon's terms, so an edit can work out what it is worth on the new total. */
  coupon?: CouponTerms | null;
  totalAmount?: number;
  total?: number;
  items?: PosSaleItem[];
  /** Who rang it up. Shown on the lists once they cover the whole shop. */
  counterStaff?: { id: string; fullName: string } | null;
  /** Who bought it, when they agreed to say. Null on a declined or older bill. */
  customerName?: string | null;
  customerPhone?: string | null;
  /**
   * These books went to the shop's other branch rather than to a customer:
   * no tender, no discount, a zero total, and out of every sales figure.
   * Optional — a server older than the field omits it, and a bill without
   * one is an ordinary sale.
   */
  isShopTransfer?: boolean;
  /** A voided bill comes back CANCELLED with voidedAt set. It stays on the record forever. */
  status?: string;
  voidedAt?: string | null;
  payment?: { method?: PaymentMethod } | null;
  /**
   * Present only on a bill paid across more than one tender: each tender and
   * what it took. Optional: a server older than split payments omits it, and
   * a bill without it was paid with the one tender `payment` names.
   */
  paymentSplits?: Array<{ method: PaymentMethod; amount: number | string }> | null;
  /** Set once the bill has been edited, at the till or in the admin panel. */
  editedAt?: string | null;
  /**
   * Every edit this bill has been through, oldest first. Optional
   * because a backend older than the bill-edit release simply omits it, and
   * the till must still show and print bills against one.
   */
  edits?: CounterBillEdit[];
  /**
   * Set while an admin has been asked to cancel this bill and has not yet
   * answered. Optional — a server older than cancel requests omits it.
   */
  cancelRequestedAt?: string | null;
  /** Every time the counter asked for this bill to be cancelled, oldest first. */
  cancelRequests?: CounterCancelRequest[];
}

/**
 * The counter asking an admin to cancel a bill.
 *
 * The counter cannot cancel a bill itself. The money goes back to the
 * customer when the request is sent, so `amount` has already left the drawer;
 * the bill keeps counting in the takings until an admin cancels it.
 */
export interface CounterCancelRequest {
  id: string;
  status: 'PENDING' | 'APPROVED' | 'REFUSED' | 'WITHDRAWN';
  reason: string;
  /** Decimal(10,2) as a string — coerce before maths. */
  amount: number | string;
  createdAt: string;
  decidedAt?: string | null;
  /** What the admin said, usually when refusing. */
  decisionNote?: string | null;
  requestedBy?: { id: string; fullName: string } | null;
  decidedBy?: { id: string; fullName: string } | null;
}

/** The request still waiting on an admin, if there is one. */
export function pendingCancelRequestOf(sale: PosSale): CounterCancelRequest | null {
  return sale.cancelRequests?.find((r) => r.status === 'PENDING') ?? null;
}

/** True while an admin has been asked to cancel this bill. */
export function isCancelRequested(sale: PosSale): boolean {
  return !isVoided(sale) && (!!sale.cancelRequestedAt || !!pendingCancelRequestOf(sale));
}

/**
 * The most recent request an admin refused, if nothing has happened since.
 * Shown on the bill so staff know the money they handed back was not covered.
 */
export function refusedCancelRequestOf(sale: PosSale): CounterCancelRequest | null {
  const requests = sale.cancelRequests ?? [];
  const last = requests[requests.length - 1];
  return last && last.status === 'REFUSED' ? last : null;
}

/** One line of a bill, as it stood before or after an edit. */
export interface CounterBillEditLine {
  bookId: string;
  title: string;
  quantity: number;
  /** Decimal(10,2) as a string — coerce before maths. */
  price: number | string;
}

/**
 * One edit made to a paid bill.
 *
 * `difference` is the money that should have crossed the counter afterwards:
 * negative was handed back to the customer, positive was collected from them.
 * Nothing in the system moves that money — a counter bill never touched a
 * gateway — so this figure and the drawer are the only record there is.
 */
export interface CounterBillEdit {
  id: string;
  /** POS was the till, under its one-a-week rules; ADMIN was the panel. */
  source: 'POS' | 'ADMIN';
  reason: string;
  itemsBefore: CounterBillEditLine[];
  itemsAfter: CounterBillEditLine[];
  totalBefore: number | string;
  totalAfter: number | string;
  difference: number | string;
  paymentMethodBefore: PaymentMethod;
  paymentMethodAfter: PaymentMethod;
  createdAt: string;
  editedBy?: { id: string; fullName: string } | null;
}

/**
 * `/pos/sales/today` returns the day's bills alongside the cash-up summary.
 * `summary.gross` deliberately excludes voided bills — it is the figure the
 * shop counts the drawer against, not a running total of everything typed.
 */
export interface TodaysSales {
  bills: PosSale[];
  /** `quantity` counts COPIES, `count` counts bills — both get asked for. */
  summary: {
    count: number;
    voided: number;
    quantity: number;
    gross: number;
    /**
     * Bills still counted in `gross` whose money was already handed back,
     * waiting on an admin to cancel them. Optional — an older server omits it.
     */
    cancelRequested?: number;
    cancelRequestedAmount?: number | string;
  };
}

export function isVoided(sale: PosSale): boolean {
  return sale.status === 'CANCELLED' || !!sale.voidedAt;
}

export function paymentMethodOf(sale: PosSale): PaymentMethod | undefined {
  return sale.paymentMethod ?? sale.payment?.method;
}

/** What to print for how a bill was paid: "CASH", or "CASH + UPI" when split. */
export function paymentSummary(sale: PosSale): string | undefined {
  if (sale.paymentSplits && sale.paymentSplits.length > 1) {
    return sale.paymentSplits.map((s) => s.method).join(' + ');
  }
  return paymentMethodOf(sale);
}

/** True once the bill has been edited — at the till or by an admin. */
export function isEdited(sale: PosSale): boolean {
  return !!sale.editedAt || (sale.edits?.length ?? 0) > 0;
}

/**
 * How long the till may edit its own bill for. Matches the server's window;
 * see `tillEditState` for who is actually the authority on it.
 */
export const EDIT_WINDOW_DAYS = 7;
const EDIT_WINDOW_MS = EDIT_WINDOW_DAYS * 24 * 60 * 60 * 1000;

export interface TillEditState {
  /** Whether to offer the button at all. */
  allowed: boolean;
  /** Why not, in the words staff should read. Null when allowed. */
  blockedBecause: string | null;
}

/**
 * Whether this bill can still be edited at the counter.
 *
 * A copy of the server's rules, and deliberately only a copy: the server
 * decides, and refuses with its own message if this gets it wrong. What this
 * buys is that staff are told "an admin has to do this one" while the customer
 * is still standing there, instead of after they have retyped the whole bill.
 *
 * Both refusals name the way forward. Past a week, or after the one edit
 * the counter gets, the bill is not frozen — it has simply moved to a desk
 * where somebody can see what changing it costs.
 */
export function tillEditState(sale: PosSale): TillEditState {
  const block = (blockedBecause: string) => ({ allowed: false, blockedBecause });

  if (isVoided(sale)) {
    return block('This bill was cancelled. Raise a fresh bill instead.');
  }

  // An edit reprices the bill and writes a tender onto it, which on a transfer
  // would turn stock that was MOVED into stock that was sold — and put it in
  // a day's takings nobody agreed to. The server refuses one outright, so the
  // only thing offering the button buys is a retyped bill and then the same no.
  if (sale.isShopTransfer) {
    return block(
      'This bill is a shop transfer, not a sale, and cannot be corrected. Cancel it and put the transfer through again.',
    );
  }

  const priorEdits = sale.edits ?? [];
  const byAdmin = priorEdits.some((edit) => edit.source === 'ADMIN');
  if (byAdmin) {
    return block(
      'An admin has already edited this bill, and their version is the final one. Any further change has to come from the admin panel.',
    );
  }
  if (priorEdits.length > 0 || sale.editedAt) {
    return block(
      'This bill has already been edited once at the counter. A second change has to come from an admin.',
    );
  }

  // A bill with no date on it is one this app cannot judge — leave it to the
  // server rather than refusing something that may well be today's.
  const raised = sale.createdAt ? Date.parse(sale.createdAt) : NaN;
  if (!Number.isNaN(raised) && Date.now() - raised > EDIT_WINDOW_MS) {
    return block(
      `This bill is more than ${EDIT_WINDOW_DAYS} days old. An admin can still change it from the admin panel.`,
    );
  }

  return { allowed: true, blockedBecause: null };
}

export interface EditSalePayload {
  /** The bill as it should read afterwards — every line, not just changed ones. */
  items: Array<{ bookId: string; quantity: number }>;
  /** One tender. Sent instead of `payments`. */
  paymentMethod?: PaymentMethod;
  /** A split across tenders; must add up to the corrected total. */
  payments?: Array<{ method: PaymentMethod; amount: number }>;
  /**
   * The discount the corrected bill should carry, sent whole like the lines
   * are. Omitting it leaves the bill's existing discount alone; sending 0
   * takes it off. The edit screen always sends a figure, so an edit there can
   * neither wipe a discount by accident nor keep one staff have removed.
   */
  discount?: number;
  /** Why. Required, and the only explanation anyone reading the bill will get. */
  reason: string;
  /** The signed-in staff member's own password, re-entered for this change. */
  password: string;
}

/** The parts of a coupon that decide what it takes off a bill. */
export interface CouponTerms {
  type: 'FLAT' | 'PERCENTAGE' | string;
  value: number | string;
  maxDiscount?: number | string | null;
  minOrderValue?: number | string | null;
}

/** What the server says a coupon code is worth on the bill on the till. */
export interface CounterCouponCheck {
  code: string;
  description: string | null;
  type: string;
  subtotal: number;
  discount: number;
}

export interface CheckCouponPayload {
  code: string;
  items: Array<{ bookId: string; quantity: number }>;
  /** E.164, `+91` + ten digits. A coupon cannot be used without one. */
  customerPhone?: string;
}

export interface CreateSalePayload {
  items: Array<{ bookId: string; quantity: number }>;
  /**
   * Omitted on a shop transfer, which nobody pays for. Required on every
   * other bill — the server rejects a sale that arrives without one.
   */
  paymentMethod?: PaymentMethod;
  /** A split across tenders, sent instead of `paymentMethod`; adds up to the total. */
  payments?: Array<{ method: PaymentMethod; amount: number }>;
  /**
   * These books are going to the shop's other branch, not to a customer. The
   * stock comes off the shelf exactly as it does on a sale, but the bill
   * carries no tender, no discount and a zero total, and every sales figure
   * leaves it out. Left off entirely when false, like `discount` and for the
   * same reason: an older server rejects the unknown field outright.
   */
  isShopTransfer?: boolean;
  /**
   * Rupees off the whole bill. Left off entirely when there is no discount,
   * rather than sent as 0 — an older server would reject the unknown field,
   * and a till that only ever omits it keeps working against one.
   */
  discount?: number;
  /**
   * A coupon code the customer brought, taken off before `discount`. Left off
   * entirely when there is none, for the same reason as `discount`. The
   * server checks it again and needs `customerPhone` with it.
   */
  couponCode?: string;
  /**
   * Given at the counter with the customer's consent, so the shop can send
   * them offers later. Both are left off entirely when the customer declines
   * — the server stores nothing rather than a blank.
   */
  customerName?: string;
  /** E.164, `+91` + ten digits. */
  customerPhone?: string;
}

// --- history ----------------------------------------------------------------

export type BillStatusFilter = 'all' | 'live' | 'voided';

export interface SalesHistoryFilters {
  /** IST calendar days, `YYYY-MM-DD`, both ends inclusive. */
  from?: string;
  to?: string;
  paymentMethod?: PaymentMethod;
  status?: BillStatusFilter;
  /** Bill-number fragment. */
  search?: string;
  page?: number;
  limit?: number;
  /** Every till's bills rather than the signed-in staff member's own. */
  allStaff?: boolean;
}

export interface SalesHistory {
  bills: PosSale[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  /**
   * Totals for the WHOLE filtered range, not the current page — a figure that
   * changed when you turned the page would be worse than none. `gross`
   * excludes voided bills; `voided` counts them separately.
   */
  summary: {
    count: number;
    voided: number;
    /** Copies sold across the whole range, not just the current page. */
    quantity: number;
    gross: number | string;
  };
}

// --- sales report -----------------------------------------------------------

/** One book on one bill: the unit a sales report is built out of. */
export interface SalesReportRow {
  billId: string;
  billNumber: string;
  soldAt: string;
  paymentMethod: PaymentMethod | null;
  /** Who was on the till for this bill. Null once that account is deleted. */
  staffId?: string | null;
  /** Absent from a server older than this column, hence the optional. */
  staffName?: string | null;
  bookId: string;
  title: string;
  sku: string | null;
  isbn13: string | null;
  quantity: number;
  /** Decimal(10,2) — arrives as a string. Always coerce. */
  amount: number | string;
}

/**
 * One payment bucket's worth of the report.
 *
 * Two of them, in the order the counter reconciles: card and UPI settle into
 * the bank on the machine's own statement, cash is counted out of the drawer
 * by hand. A bucket with no sales still comes back, empty — dropping it would
 * make "nothing sold this way" and "the section is missing" look identical.
 */
export interface SalesReportGroup {
  key: 'DIGITAL' | 'CASH';
  label: string;
  methods: PaymentMethod[];
  rows: SalesReportRow[];
  totals: SalesReportTotals;
}

export interface SalesReportTotals {
  bills: number;
  /** Distinct books, as against `quantity`, which counts copies. */
  titles: number;
  quantity: number;
  /** What the books listed at — the rows above, added up. */
  amount: number | string;
  /**
   * Given away as discounts across whole bills. Optional: a server older than
   * counter discounts sends neither this nor `net`, and a period with nothing
   * discounted reports zero.
   */
  discount?: number | string;
  /** `amount` less `discount` — the figure the drawer is counted against. */
  net?: number | string;
}

/**
 * Books carried to the shop's other branch in the period. Not a sale, so the
 * rows carry no amount at all — the section exists so stock that left the
 * shelf is still accounted for, not so it can be added up.
 */
export interface SalesReportTransfers {
  rows: Array<{
    billId: string;
    billNumber: string;
    movedAt: string;
    staffName: string | null;
    bookId: string;
    title: string;
    sku: string | null;
    isbn13: string | null;
    quantity: number;
  }>;
  totals: { bills: number; titles: number; quantity: number };
}

export interface SalesReport {
  /** The IST calendar days the report covers, both inclusive. */
  from: string;
  to: string;
  /** Whose till, when a staff member reports their own sales. Null for admins. */
  staffName: string | null;
  groups: SalesReportGroup[];
  /**
   * `voided` is reported but is NOT part of any other figure here: a reversed
   * bill put its stock back, so counting it would overstate the day.
   */
  totals: SalesReportTotals & { voided: number };
  /** Absent from a server older than shop transfers, hence every read guarded. */
  transfers?: SalesReportTransfers;
}

export interface SalesReportFilters {
  /** IST calendar days, `YYYY-MM-DD`, both ends inclusive. Defaults to today. */
  from?: string;
  to?: string;
}

// --- store shelf ------------------------------------------------------------

export interface StoreStockBook {
  id: string;
  title: string;
  sku: string | null;
  isbn13: string | null;
  coverImageUrl: string | null;
  /** Decimal(10,2) — arrives as a string. Always coerce. */
  mrp: number | string;
  price: number | string;
  /** @deprecated Mirror of `stockQuantity` — read via `shelfStockOf`. */
  storeStock?: number;
  /** The shop's stock. One figure, shared with the website. */
  stockQuantity?: number;
  /**
   * The sections this book sits in, as plain names. A book can be in more than
   * one and plenty are in none, so never assume there is exactly one.
   */
  categories?: string[];
}

/**
 * The shelf row's stock figure. Same both-names caution as `stockCountOf`:
 * `storeStock` is what an older server calls the counter's sellable pool, and
 * what the current one mirrors the shared figure into.
 */
export function shelfStockOf(book: StoreStockBook): number {
  return Number(book.storeStock ?? book.stockQuantity ?? 0) || 0;
}

export interface StoreStock {
  books: StoreStockBook[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  summary: { titlesInStore: number; copiesInStore: number };
}

// --- online orders ----------------------------------------------------------

/** Which of the website's orders the list shows. */
export type OnlineOrderView = 'open' | 'shipped' | 'all';

/**
 * One of the website's orders, as much of it as the counter needs to pack it:
 * what goes in the parcel and where the parcel is going. No money, no payment
 * and no customer account come with it — the till cannot change an online
 * order, only read it and print its address.
 */
export interface OnlineOrder {
  id: string;
  orderNumber: string;
  createdAt: string;
  /** PLACED, CONFIRMED, SHIPPED, OUT_FOR_DELIVERY, DELIVERED, CANCELLED… */
  status: string;
  /** POSTAL, SAME_DAY or TWO_HOUR. */
  deliveryType: string;
  items: Array<{ id: string; title: string; quantity: number }>;
  shippingAddress: {
    recipientName: string;
    phone: string | null;
    /** Already split the way the slip prints them, one line each. */
    lines: string[];
  };
}

export interface OnlineOrders {
  orders: OnlineOrder[];
  /**
   * The shop's own address, the sender on every parcel — sent once with the
   * list. Absent from a server older than this screen.
   */
  fromAddress?: OnlineOrder['shippingAddress'];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Coerce a Decimal-as-string (or anything) into a usable number. */
export function amount(value: unknown): number {
  return num(value) ?? 0;
}

/** What the shop is giving away against MRP, as a whole-number percentage. */
export function discountPercentOf(book: StoreStockBook): number {
  const mrp = amount(book.mrp);
  const price = amount(book.price);
  if (mrp <= 0 || price >= mrp) return 0;
  return Math.round(((mrp - price) / mrp) * 100);
}

function reportQuery(filters: SalesReportFilters): string {
  const q = new URLSearchParams();
  if (filters.from) q.set('from', filters.from);
  if (filters.to) q.set('to', filters.to);
  const qs = q.toString();
  return qs ? `?${qs}` : '';
}

export const posApi = {
  lookup: (q: string, signal?: AbortSignal) =>
    api.get<PosBook[]>(`/pos/lookup?q=${encodeURIComponent(q)}`, signal),

  createSale: (payload: CreateSalePayload) => api.post<PosSale>('/pos/sales', payload),

  /** What a coupon code would take off this bill, or why it cannot be used. */
  /**
   * The name a mobile number gave on its last counter bill, for filling the
   * customer's details in on a return visit. Both null for a new number.
   */
  counterCustomer: (phone: string, signal?: AbortSignal) =>
    api.get<{ name: string | null; lastVisit: string | null }>(
      `/pos/customer?phone=${encodeURIComponent(phone)}`,
      signal,
    ),

  checkCoupon: (payload: CheckCouponPayload) =>
    api.post<CounterCouponCheck>('/pos/coupons/check', payload),

  /**
   * Today's bills. A staff member gets their own unless `allStaff` is set;
   * the server always gives an admin every till.
   */
  todaysSales: (allStaff = false) =>
    api.get<TodaysSales>(`/pos/sales/today${allStaff ? '?allStaff=true' : ''}`),

  /** Bill history, scoped the same way as `todaysSales`. */
  salesHistory: (filters: SalesHistoryFilters, signal?: AbortSignal) => {
    const q = new URLSearchParams();
    if (filters.allStaff) q.set('allStaff', 'true');
    if (filters.from) q.set('from', filters.from);
    if (filters.to) q.set('to', filters.to);
    if (filters.paymentMethod) q.set('paymentMethod', filters.paymentMethod);
    if (filters.status && filters.status !== 'all') q.set('status', filters.status);
    if (filters.search?.trim()) q.set('search', filters.search.trim());
    q.set('page', String(filters.page ?? 1));
    q.set('limit', String(filters.limit ?? 25));
    return api.get<SalesHistory>(`/pos/sales?${q.toString()}`, signal);
  },

  /**
   * What was SOLD over a range, one row per title.
   *
   * Not derivable from `salesHistory`: that pages at 25 bills, so adding up
   * its lines on the client would report a page rather than the range. The
   * server aggregates the whole range in one query instead.
   */
  salesReport: (filters: SalesReportFilters, signal?: AbortSignal) =>
    api.get<SalesReport>(`/pos/sales/report${reportQuery(filters)}`, signal),

  /**
   * The same report as a PDF, rendered by the server.
   *
   * Server-side for the same reason the receipt is: the sheet then looks the
   * same however it was asked for, and carries the shop's letterhead, which
   * printing the DOM does not.
   */
  salesReportPdf: (filters: SalesReportFilters) =>
    api.blob(`/pos/sales/report/pdf${reportQuery(filters)}`),

  /** What is on the shop floor right now. */
  storeStock: (
    params: { search?: string; includeOutOfStock?: boolean; page?: number; limit?: number },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    if (params.search?.trim()) q.set('search', params.search.trim());
    if (params.includeOutOfStock) q.set('includeOutOfStock', 'true');
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 50));
    return api.get<StoreStock>(`/pos/stock?${q.toString()}`, signal);
  },

  /** The website's orders, for packing. Always the whole shop's. */
  onlineOrders: (
    params: { status?: OnlineOrderView; search?: string; page?: number; limit?: number },
    signal?: AbortSignal,
  ) => {
    const q = new URLSearchParams();
    q.set('status', params.status ?? 'open');
    if (params.search?.trim()) q.set('search', params.search.trim());
    q.set('page', String(params.page ?? 1));
    q.set('limit', String(params.limit ?? 25));
    return api.get<OnlineOrders>(`/pos/online-orders?${q.toString()}`, signal);
  },

  /**
   * An online order's shipping address as a slip for the till roll — the
   * recipient, the address and a phone number, and nothing else. Rendered by
   * the server at the roll's width, for the reason the receipt is.
   */
  addressLabel: (id: string) =>
    api.blob(`/pos/online-orders/${encodeURIComponent(id)}/address-label`),

  /** The shop's own address as a slip for the till roll, headed FROM. */
  fromAddressLabel: () => api.blob('/pos/online-orders/from-address-label'),

  saleById: (id: string) => api.get<PosSale>(`/pos/sales/${encodeURIComponent(id)}`),

  /**
   * Edit a bill that has already been paid.
   *
   * The lines go up whole rather than as a diff — what is sent is what the
   * bill will say afterwards. The server works out what actually moved, takes
   * or returns exactly that much stock, and refuses the whole change if the
   * shelf cannot cover it, the same way it refuses a sale.
   *
   * The money is NOT moved by this call and there is nothing to move it
   * through: no counter bill ever touched a gateway. What comes back records
   * the difference; handing it over or collecting it is done by hand at the
   * counter, as a void's refund is.
   */
  editSale: (id: string, payload: EditSalePayload) =>
    api.patch<PosSale>(`/pos/sales/${encodeURIComponent(id)}`, payload),

  /**
   * Ask an admin to cancel a bill. The counter cannot cancel one itself.
   * The admins are emailed and the bill is flagged; nothing else about it
   * changes until an admin decides.
   */
  requestCancel: (id: string, reason: string) =>
    api.post<PosSale>(`/pos/sales/${encodeURIComponent(id)}/cancel-request`, { reason }),

  /** Take a cancel request back — the customer kept the books after all. */
  withdrawCancelRequest: (id: string) =>
    api.post<PosSale>(`/pos/sales/${encodeURIComponent(id)}/cancel-request/withdraw`, {}),

  /**
   * The receipt, rendered by the server.
   *
   * `thermal` comes back sized to an 80mm roll so it prints 1:1 with no
   * scaling dialog; `a4` is the same receipt on a sheet for a customer who
   * wants one to keep. Rendering server-side rather than printing the DOM is
   * what makes the paper width predictable — browser print CSS was the part
   * that kept coming out wrong.
   */
  receipt: (id: string, format: 'thermal' | 'a4' = 'thermal') =>
    api.blob(
      `/pos/sales/${encodeURIComponent(id)}/receipt?format=${format}`,
    ),
};

/**
 * Send a receipt to the printer.
 *
 * The PDF is loaded into a hidden iframe and printed from there, so the
 * staff member gets the print dialog directly over the till screen instead of a
 * file appearing in Downloads that somebody then has to find and open.
 */
export async function printReceipt(
  id: string,
  format: 'thermal' | 'a4' = 'thermal',
): Promise<void> {
  await printPdf(await posApi.receipt(id, format));
}

/**
 * Send an online order's shipping address to the till printer, on its own —
 * the slip that goes on the parcel.
 */
export async function printAddressLabel(id: string): Promise<void> {
  await printPdf(await posApi.addressLabel(id));
}

/** Send the shop's own address to the till printer — the FROM slip. */
export async function printFromAddressLabel(): Promise<void> {
  await printPdf(await posApi.fromAddressLabel());
}

async function printPdf(blob: Blob): Promise<void> {
  const url = URL.createObjectURL(blob);

  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.src = url;

  await new Promise<void>((resolve) => {
    frame.onload = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        // Some browsers refuse to drive print() on a blob frame. Falling back
        // to a tab still gets the receipt in front of the staff member.
        window.open(url, '_blank');
      }
      resolve();
    };
    document.body.appendChild(frame);
  });

  // Give the print dialog time to take its copy of the document before the
  // object URL and the frame go away underneath it.
  window.setTimeout(() => {
    frame.remove();
    URL.revokeObjectURL(url);
  }, 60_000);
}

/** Save the receipt as a PDF file — for a customer who asks for a copy. */
export async function downloadReceipt(
  id: string,
  billNumber: string,
  format: 'thermal' | 'a4' = 'a4',
): Promise<void> {
  const blob = await posApi.receipt(id, format);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${billNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

// --- sales report export ----------------------------------------------------

/**
 * One CSV field.
 *
 * Book titles carry commas, quotes and the occasional line break, any one of
 * which splits a row into the wrong number of columns if it goes out bare.
 * Quoting anything that contains one and doubling embedded quotes is the whole
 * of the CSV escaping rule.
 */
function csvField(value: string | number): string {
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const csvRow = (cells: Array<string | number>) => cells.map(csvField).join(',');

/** `20 Aug 2026`, or `1 Aug 2026 – 20 Aug 2026` when the range spans days. */
export function reportPeriodLabel(from: string, to: string): string {
  const day = (iso: string) =>
    new Date(`${iso}T00:00:00+05:30`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
  return from === to ? day(from) : `${day(from)} – ${day(to)}`;
}

/** `blossom-sales-report-2026-08-20` / `…-2026-08-01_to_2026-08-20`. */
function reportFileStem(report: SalesReport): string {
  const span =
    report.from === report.to ? report.from : `${report.from}_to_${report.to}`;
  return `blossom-sales-report-${span}`;
}

function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * The report as a spreadsheet.
 *
 * Money and quantities go out as bare numbers — no ₹, no thousands separator —
 * so Excel reads them as numbers and the columns can be summed, sorted and
 * pivoted. The currency lives in the column heading instead. The payment
 * sections are stacked one after another with their own subtotal rows, the
 * same shape as the printed sheet, and each row also carries its section in a
 * column so a filter on the spreadsheet can pull one bucket back out.
 */
export function reportToCsv(report: SalesReport, storeName: string): string {
  const rows: string[] = [
    csvRow([`${storeName} — sales report`]),
    csvRow(['Period', reportPeriodLabel(report.from, report.to)]),
  ];
  if (report.staffName) rows.push(csvRow(['Counter', report.staffName]));
  rows.push(
    csvRow(['Bills', report.totals.bills]),
    csvRow(['Titles', report.totals.titles]),
    csvRow(['Copies sold', report.totals.quantity]),
    csvRow(['Total (INR)', amount(report.totals.amount).toFixed(2)]),
  );
  // Two more rows, and only when there is something to say — a sheet whose
  // total does not match the drawer is worse than one that never mentions
  // discounts at all.
  if (amount(report.totals.discount) > 0) {
    rows.push(
      csvRow(['Discount (INR)', amount(report.totals.discount).toFixed(2)]),
      csvRow([
        'Net taken (INR)',
        amount(report.totals.net ?? report.totals.amount).toFixed(2),
      ]),
    );
  }
  if (report.totals.voided > 0) {
    rows.push(csvRow(['Cancelled bills (excluded)', report.totals.voided]));
  }

  const HEAD = ['Payment', 'Bill no.', 'Time', 'Billed by', 'Book title', 'Code', 'Quantity', 'Amount (INR)'];

  report.groups.forEach((group) => {
    rows.push('');
    rows.push(csvRow([group.label]));
    rows.push(csvRow(HEAD));
    if (group.rows.length === 0) {
      rows.push(csvRow([group.label, '', '', '', 'No sales taken this way', '', 0, '0.00']));
    }
    group.rows.forEach((row) => {
      rows.push(
        csvRow([
          group.label,
          row.billNumber,
          billTime(row.soldAt),
          // Repeated on every line of a bill here, unlike on screen and on
          // paper: a spreadsheet gets filtered and pivoted by this column, and
          // a value that only appears on a bill's first row would drop every
          // other line of that bill out of the result.
          row.staffName ?? '',
          row.title,
          row.sku ?? row.isbn13 ?? '',
          row.quantity,
          amount(row.amount).toFixed(2),
        ]),
      );
    });
    rows.push(
      csvRow([
        '',
        `${group.label} total`,
        '',
        '',
        `${group.totals.bills} ${group.totals.bills === 1 ? 'bill' : 'bills'}`,
        '',
        group.totals.quantity,
        amount(group.totals.amount).toFixed(2),
      ]),
    );
  });

  rows.push('');
  rows.push(
    csvRow([
      '',
      'GRAND TOTAL',
      '',
      '',
      '',
      '',
      report.totals.quantity,
      amount(report.totals.amount).toFixed(2),
    ]),
  );

  // Its own block at the foot, after the grand total and in none of it. The
  // Amount column is left empty rather than filled with 0.00 — a zero would be
  // summed by whoever pivots this sheet, which is exactly the mistake the
  // section exists to prevent.
  const transfers = report.transfers;
  if (transfers && transfers.rows.length > 0) {
    rows.push('');
    rows.push(csvRow(['Shop transfer — moved to the other shop, not sold']));
    rows.push(csvRow(HEAD));
    transfers.rows.forEach((row) => {
      rows.push(
        csvRow([
          'Shop transfer',
          row.billNumber,
          billTime(row.movedAt),
          row.staffName ?? '',
          row.title,
          row.sku ?? row.isbn13 ?? '',
          row.quantity,
          '',
        ]),
      );
    });
    rows.push(
      csvRow([
        '',
        'Transferred',
        '',
        '',
        `${transfers.totals.bills} ${transfers.totals.bills === 1 ? 'bill' : 'bills'}`,
        '',
        transfers.totals.quantity,
        '',
      ]),
    );
  }

  // CRLF because that is what Excel expects from a .csv, and a BOM because
  // without one Excel decodes the file as the local ANSI codepage and mangles
  // every non-ASCII character in a title.
  return `﻿${rows.join('\r\n')}\r\n`;
}

/** Save the report as a .csv the counter can open in Excel. */
export function downloadReportCsv(report: SalesReport, storeName: string): void {
  saveBlob(
    new Blob([reportToCsv(report, storeName)], {
      type: 'text/csv;charset=utf-8;',
    }),
    `${reportFileStem(report)}.csv`,
  );
}

/** Save the report as a PDF — the sheet, laid out and rendered by the server. */
export async function downloadReportPdf(report: SalesReport): Promise<void> {
  const blob = await posApi.salesReportPdf({ from: report.from, to: report.to });
  saveBlob(blob, `${reportFileStem(report)}.pdf`);
}

// --- tolerant readers -------------------------------------------------------

/**
 * Money arrives as a STRING, not a number.
 *
 * Every amount on an order is Decimal(10,2) in Postgres, and the API serialises
 * those as strings ("500", "250.00") to avoid float rounding on the way out.
 * A `typeof x === 'number'` test therefore rejects every real total and falls
 * through to zero, which renders the entire bill as ₹0.00 — so every read of an
 * amount goes through here.
 */
function num(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function billNumberOf(sale: PosSale): string {
  return sale.billNumber ?? sale.orderNumber ?? sale.id;
}

export function saleTotalOf(sale: PosSale): number {
  return (
    num(sale.totalAmount) ??
    num(sale.total) ??
    num(sale.subtotal) ??
    (sale.items ?? []).reduce((sum, item) => sum + itemTotalOf(item), 0)
  );
}

/** What the lines on a bill add up to, before any discount. */
export function saleSubtotalOf(sale: PosSale): number {
  return (
    num(sale.subtotal) ??
    (sale.items ?? []).reduce((sum, item) => sum + itemTotalOf(item), 0)
  );
}

/**
 * The flat sum knocked off this bill at the counter, or 0.
 *
 * Derived from the two totals when the server sends no discount field, so a
 * bill raised against an older server — where `total` is simply the subtotal —
 * still reads as no discount rather than as a mystery.
 */
export function saleDiscountOf(sale: PosSale): number {
  const stated = num(sale.discount);
  if (stated !== null) return stated;
  const gap = saleSubtotalOf(sale) - saleTotalOf(sale);
  return gap > 0 ? gap : 0;
}

/** The coupon on this bill and what it took off, or null when there was none. */
export function saleCouponOf(sale: PosSale): { code: string; amount: number } | null {
  const amount = num(sale.couponDiscount) ?? 0;
  if (amount <= 0) return null;
  return { code: sale.couponCode ?? '', amount };
}

/**
 * What a coupon takes off a given subtotal — the server's rule, repeated so an
 * edit can show the new figure before it is saved. The server works it out
 * again on save and its number is the one the bill keeps.
 */
export function couponWorth(terms: CouponTerms, subtotal: number): number {
  let off: number;
  if (terms.type === 'FLAT') {
    off = Number(terms.value);
  } else {
    off = (subtotal * Number(terms.value)) / 100;
    if (terms.maxDiscount != null) off = Math.min(off, Number(terms.maxDiscount));
  }
  off = Math.min(off, subtotal);
  return Math.round(off * 100) / 100;
}

/** Copies on a bill, across all of its lines. */
export function billCopyCount(sale: PosSale): number {
  return (sale.items ?? []).reduce((n, item) => n + item.quantity, 0);
}

/**
 * A bill's lines as "2 × Title", for the summary under a bill row.
 *
 * The quantity is prefixed even when it is 1: in a list where some rows carry
 * a number and some do not, the bare ones read as though something is missing.
 */
export function billLineLabels(sale: PosSale): string[] {
  return (sale.items ?? []).map(
    (item) => `${item.quantity} × ${itemTitleOf(item)}`,
  );
}

export function itemTitleOf(item: PosSaleItem): string {
  return item.title ?? item.bookTitle ?? item.book?.title ?? 'Item';
}

export function itemCodeOf(item: PosSaleItem): string {
  return item.sku ?? item.book?.sku ?? item.isbn13 ?? item.book?.isbn13 ?? '';
}

export function itemUnitPriceOf(item: PosSaleItem): number {
  const unit = num(item.unitPrice) ?? num(item.price);
  if (unit !== null) return unit;
  const total = num(item.totalPrice) ?? num(item.lineTotal);
  if (total !== null && item.quantity > 0) return total / item.quantity;
  return 0;
}

export function itemTotalOf(item: PosSaleItem): number {
  return (
    num(item.totalPrice) ??
    num(item.lineTotal) ??
    itemUnitPriceOf(item) * item.quantity
  );
}
